import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { workerLogin } from '../api';
import './WorkerLogin.css';

/**
 * Worker Login Component
 * Allows workers to login and access their submissions
 */
function WorkerLogin({ onLogin }) {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    username: '',
    password: ''
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      const response = await workerLogin(formData.username, formData.password);

      if (response.success) {
        // Store worker info in localStorage
        localStorage.setItem('worker_user', JSON.stringify(response.worker));

        // Call parent callback
        if (onLogin) {
          onLogin(response.worker);
        }

        // Redirect to worker dashboard
        navigate('/worker/dashboard');
      }
    } catch (err) {
      setError(err.message || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="worker-login-page">
      {/* Header */}
      <header className="worker-login-header">
        <div className="worker-login-header-container">
          <div className="brand">
            <img src="/logo-small.png" alt="SSM" className="logo" />
            <span className="brand-name">SSM PORTAL</span>
          </div>
          <button onClick={() => navigate('/')} className="btn-back">
            ← Back to Home
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="worker-login-main">
        <div className="worker-login-container">
          <div className="worker-login-card">
            {/* Title */}
            <div className="worker-login-title-section">
              <h1 className="worker-login-title">👷 Worker Login</h1>
              <p className="worker-login-subtitle">
                Sign in to access your submissions
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="alert alert-error">
                <div className="alert-content">
                  ✕ {error}
                </div>
              </div>
            )}

            {/* Login Form */}
            <form onSubmit={handleSubmit} className="worker-login-form">
              <div className="form-group">
                <label htmlFor="username" className="form-label">
                  Username
                </label>
                <input
                  type="text"
                  id="username"
                  name="username"
                  value={formData.username}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your username"
                  required
                  autoFocus
                />
              </div>

              <div className="form-group">
                <label htmlFor="password" className="form-label">
                  Password
                </label>
                <input
                  type="password"
                  id="password"
                  name="password"
                  value={formData.password}
                  onChange={handleInputChange}
                  className="form-input"
                  placeholder="Enter your password"
                  required
                />
              </div>

              <button
                type="submit"
                className="btn-login"
                disabled={loading}
              >
                {loading ? (
                  <>
                    <span>Signing in</span>
                    <div className="loading-dots">
                      <span className="dot"></span>
                      <span className="dot"></span>
                      <span className="dot"></span>
                    </div>
                  </>
                ) : (
                  <>
                    <span>Sign In</span>
                    <svg className="btn-arrow" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
                      <line x1="5" y1="12" x2="19" y2="12"/>
                      <polyline points="12 5 19 12 12 19"/>
                    </svg>
                  </>
                )}
              </button>
            </form>

            {/* Info */}
            <div className="worker-login-info">
              <p>Don't have an account? Contact your administrator.</p>
            </div>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="worker-login-footer">
        <div className="footer-content">
          <p>📞 Need help? Call us at <a href="tel:+12124988863">+1 (212)-498-8863</a></p>
          <p>© 2025 Skilled Safety Management. All Rights Reserved.</p>
        </div>
      </footer>
    </div>
  );
}

export default WorkerLogin;
